import React, { useEffect, useRef, useState, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import gsap from 'gsap'
import introVideo from '@/assets/home/intro-loader-bg.mp4'
import logoPng from '@/assets/logo.png'
import SystemLogo from '../SystemLogo'
import './index.css'

export interface IntroLoaderProps {
  onComplete?: () => void
}

const INTRO_PLAYBACK_SECONDS = 3
const PROGRESS_DURATION_SECONDS = 1.5

export default function IntroLoader({ onComplete }: IntroLoaderProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const copyRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const progressFillRef = useRef<HTMLSpanElement>(null)
  const whiteBridgeRef = useRef<HTMLDivElement>(null)

  const [progressValue, setProgressValue] = useState(0)
  const [mediaError, setMediaError] = useState(false)

  const copyTimelineRef = useRef<gsap.core.Timeline | null>(null)
  const exitTimelineRef = useRef<gsap.core.Timeline | null>(null)
  const fallbackTimerRef = useRef<number | null>(null)

  const hasStartedRef = useRef(false)
  const hasCompletedRef = useRef(false)

  const finishIntro = useCallback(({ immediate = false } = {}) => {
    if (hasCompletedRef.current) return
    hasCompletedRef.current = true

    if (fallbackTimerRef.current !== null) {
      window.clearTimeout(fallbackTimerRef.current)
    }
    copyTimelineRef.current?.kill()
    exitTimelineRef.current?.kill()

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const instant = immediate || reduceMotion
    setProgressValue(100)

    if (progressFillRef.current) {
      gsap.set(progressFillRef.current, { scaleX: 1 })
    }

    exitTimelineRef.current = gsap.timeline({
      onComplete: () => {
        onComplete?.()
      },
    })
      .set(whiteBridgeRef.current, { autoAlpha: 1 })
      .to(rootRef.current, {
        autoAlpha: 0,
        duration: instant ? 0.08 : 1.05,
        ease: 'power2.inOut',
      }, instant ? 0 : 0.16)
  }, [onComplete])

  const startCopyAnimation = useCallback(() => {
    if (hasStartedRef.current || hasCompletedRef.current || !copyRef.current) return
    hasStartedRef.current = true

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(copyRef.current, { autoAlpha: 1, scale: 1 })
      return
    }

    const revealTexts = copyRef.current.querySelectorAll('[data-copy-text]')
    const brand = copyRef.current.querySelector('.intro-loader__brand-name')
    const headline = copyRef.current.querySelector('.intro-loader__headline')
    const welcomeText = copyRef.current.querySelector('.intro-loader__welcome [data-copy-text]')
    const taglineText = copyRef.current.querySelector('.intro-loader__tagline [data-copy-text]')
    const signature = copyRef.current.querySelector('.intro-loader__signature')

    copyTimelineRef.current = gsap.timeline()
      .fromTo(copyRef.current, {
        autoAlpha: 0,
      }, {
        autoAlpha: 1,
        duration: 0.18,
      })
      .fromTo(revealTexts, {
        yPercent: 110,
        autoAlpha: 0,
      }, {
        yPercent: 0,
        autoAlpha: 1,
        duration: 0.78,
        stagger: 0.18,
        ease: 'power3.out',
      }, 0.06)
      .fromTo(brand, {
        scale: 0.82,
        autoAlpha: 0,
      }, {
        scale: 1,
        autoAlpha: 1,
        duration: 0.96,
        ease: 'power3.out',
      }, 0.18)
      .fromTo(signature, {
        scaleX: 0,
        autoAlpha: 0,
      }, {
        scaleX: 1,
        autoAlpha: 1,
        duration: 0.72,
        ease: 'power2.out',
      }, 0.58)
      .fromTo(progressRef.current, {
        y: 10,
        autoAlpha: 0,
      }, {
        y: 0,
        autoAlpha: 1,
        duration: 0.55,
        ease: 'power2.out',
      }, 0.42)
      .fromTo(progressFillRef.current, {
        scaleX: 0,
      }, {
        scaleX: 1,
        duration: PROGRESS_DURATION_SECONDS,
        ease: 'none',
      }, 0)
      .to(welcomeText, {
        y: -16,
        autoAlpha: 0,
        duration: 0.4,
        ease: 'power2.in',
      }, 1.95)
      .to(taglineText, {
        y: 16,
        autoAlpha: 0,
        duration: 0.42,
        ease: 'power2.in',
      }, 2.02)
      .to(signature, {
        scaleX: 0,
        autoAlpha: 0,
        duration: 0.38,
        ease: 'power2.in',
      }, 2.04)
      .to(headline, {
        y: -8,
        scale: 1.14,
        autoAlpha: 0,
        duration: 0.72,
        ease: 'power2.inOut',
        force3D: true,
      }, 2.08)
      .to(progressRef.current, {
        y: 12,
        autoAlpha: 0,
        duration: 0.55,
        ease: 'power2.in',
      }, 2.12)
  }, [])

  const updateProgress = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    const progressTime = Math.min(video.currentTime, PROGRESS_DURATION_SECONDS)
    setProgressValue(Math.min(100, (progressTime / PROGRESS_DURATION_SECONDS) * 100))

    if (video.currentTime >= INTRO_PLAYBACK_SECONDS) {
      video.pause()
      video.currentTime = INTRO_PLAYBACK_SECONDS
      finishIntro()
    }
  }, [finishIntro])

  const handleEnded = useCallback(() => {
    finishIntro()
  }, [finishIntro])

  const handleMediaError = useCallback(() => {
    setMediaError(true)
    if (fallbackTimerRef.current !== null) {
      window.clearTimeout(fallbackTimerRef.current)
    }
    if (copyRef.current) gsap.to(copyRef.current, { autoAlpha: 0, duration: 0.3 })
    if (progressRef.current) gsap.to(progressRef.current, { autoAlpha: 0, duration: 0.3 })
  }, [])

  const continueWithoutVideo = useCallback(() => {
    finishIntro({ immediate: true })
  }, [finishIntro])

  useEffect(() => {
    gsap.set(rootRef.current, { autoAlpha: 1 })
    gsap.set([copyRef.current, progressRef.current], { autoAlpha: 0 })
    gsap.set(progressFillRef.current, { scaleX: 0 })
    gsap.set(whiteBridgeRef.current, { autoAlpha: 0 })

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fallbackTimerRef.current = window.setTimeout(() => finishIntro({ immediate: true }), 450)
      return
    }

    fallbackTimerRef.current = window.setTimeout(() => finishIntro(), 8000)

    const playPromise = videoRef.current?.play()
    playPromise?.catch(() => {
      setMediaError(true)
    })

    return () => {
      if (fallbackTimerRef.current !== null) {
        window.clearTimeout(fallbackTimerRef.current)
      }
      copyTimelineRef.current?.kill()
      exitTimelineRef.current?.kill()
    }
  }, [finishIntro])

  return (
    <section
      ref={rootRef}
      className="intro-loader"
      aria-label="谋势社区开场动画"
      aria-live="polite"
    >
      <video
        ref={videoRef}
        className="intro-loader__video"
        src={introVideo}
        muted
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload no-picture-in-picture"
        preload="auto"
        onPlaying={startCopyAnimation}
        onTimeUpdate={updateProgress}
        onEnded={handleEnded}
        onError={handleMediaError}
      />

      <div ref={copyRef} className="intro-loader__copy">
        <p className="intro-loader__welcome">
          <span data-copy-text>欢迎抵达</span>
        </p>
        <h1 className="intro-loader__headline">
          <div className="intro-loader__brand-name flex items-center justify-center">
            <img src={logoPng} alt="谋势" className="h-16 sm:h-20 md:h-24 lg:h-32 xl:h-36 2xl:h-40 w-auto object-contain" />
          </div>
          <span className="intro-loader__signature" aria-hidden="true" />
        </h1>
        <p className="intro-loader__tagline">
          <span data-copy-text>灵感在此汇聚，好故事由此生长</span>
        </p>
      </div>

      <div ref={progressRef} className="intro-loader__progress" aria-hidden="true">
        <div className="intro-loader__progress-meta">
          <span>灵感正在登陆</span>
          <strong>{Math.round(progressValue)}%</strong>
        </div>
        <div className="intro-loader__progress-rule">
          <span ref={progressFillRef} />
        </div>
      </div>

      {mediaError && (
        <div className="intro-loader__error" role="alert">
          <AlertCircle size={28} aria-hidden="true" />
          <p>背景视频暂时无法播放</p>
          <button type="button" onClick={continueWithoutVideo}>直接进入首页</button>
        </div>
      )}

      <div ref={whiteBridgeRef} className="intro-loader__white-bridge" aria-hidden="true" />
    </section>
  )
}
